// ============================================================
//  Bragsheet React — utils/offline.js
//  Shared offline-save utilities. Firestore persistence (see firestore.js) keeps
//  a local cache and queues writes made offline, syncing them on reconnect — but
//  a write's promise only settles on SERVER ack, so offline it never settles.
//  These helpers are the ONE place the app decides "am I offline", time-boxes a
//  network action, races a queueable write, and words the user-facing messages —
//  no per-screen copies, no new dependencies (navigator.onLine + a tiny pub/sub).
//
//  Two write categories (see docs/offline-persistence-brief.md):
//    A — own-data writes safe to queue: use queueableWrite(); offline returns a
//        `queued` success instead of hanging; a later server rejection is
//        surfaced (never an unhandled rejection).
//    B — writes that genuinely need the network: check isOffline() FIRST and, if
//        the attempt is made, wrap it in withOnlineTimeout() so a dead link
//        (navigator.onLine lies on "wifi, no data") can never spin forever.
// ============================================================

import { useSyncExternalStore } from 'react';

export const OFFLINE_MSG = "You're offline. This needs an internet connection.";
export const TIMEOUT_MSG = 'This is taking too long. Check your connection and try again.';
export const QUEUED_MSG = "Saved on your phone — it'll sync when you're back online";

/** Best-effort offline check. navigator.onLine can be a FALSE POSITIVE (connected
 *  to wifi with no real data path), so Category-B actions must ALSO time-box the
 *  call via withOnlineTimeout — this only cheaply catches the obvious case. */
export function isOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

// ── live online/offline status hook ──
function subscribeOnline(cb) {
  window.addEventListener('online', cb);
  window.addEventListener('offline', cb);
  return () => {
    window.removeEventListener('online', cb);
    window.removeEventListener('offline', cb);
  };
}
export function useOnline() {
  return useSyncExternalStore(
    subscribeOnline,
    () => (typeof navigator === 'undefined' ? true : navigator.onLine),
    () => true,
  );
}

// ── sync-failure notifier: a queued write the server later rejected (rule 4) ──
const syncListeners = new Set();
/** Subscribe to queued-write failures. Returns an unsubscribe fn. */
export function subscribeSyncFailure(cb) {
  syncListeners.add(cb);
  return () => syncListeners.delete(cb);
}
/** Broadcast a queued-write failure to the mounted SyncToast (best-effort). */
export function notifySyncFailure(msg) {
  for (const cb of syncListeners) {
    try { cb(msg); } catch { /* one bad listener must not break the rest */ }
  }
}

/**
 * Run a Firestore write that is safe to queue offline (Category A). Returns
 * `{ queued }`.
 *   - Online + success  → { queued: false }.
 *   - Online + failure   → REJECTS, so the caller shows the error. Online
 *     failures are NEVER swallowed (rule 1).
 *   - Offline / no ack within graceMs → { queued: true }. The write keeps
 *     syncing; if the server later rejects it, `onLater` runs and, by default, a
 *     toast is shown — never an unhandled promise rejection (rule 4).
 * @param {() => Promise<any>} doWrite starts the write and returns its promise
 * @param {{ graceMs?: number, label?: string, onLater?: (err:any)=>void }} [opts]
 * @returns {Promise<{ queued: boolean }>}
 */
export async function queueableWrite(doWrite, { graceMs = 3500, label = 'change', onLater } = {}) {
  const write = Promise.resolve().then(doWrite);
  const QUEUED = Symbol('queued');
  const outcome = await Promise.race([
    write.then(() => 'acked'),
    new Promise((resolve) => setTimeout(() => resolve(QUEUED), graceMs)),
  ]);
  if (outcome === QUEUED) {
    // Attach the late handler synchronously so the write's eventual rejection is
    // always handled (no unhandled rejection), whichever way it settles.
    write.catch((err) => {
      console.warn(`[offline] queued ${label} later failed:`, err);
      if (onLater) {
        try { onLater(err); } catch (e) { console.warn('[offline] onLater threw:', e); }
      } else {
        notifySyncFailure(`Couldn't sync your ${label}. It'll retry — or try again.`);
      }
    });
    return { queued: true };
  }
  return { queued: false };
}

/**
 * Time-box a network action for Category-B flows. Rejects with a timeout error
 * (`err.code === 'offline/timeout'`, message = TIMEOUT_MSG) if `promise` hasn't
 * settled within `ms`, so the caller can stop the spinner and show TIMEOUT_MSG.
 * It does NOT cancel the underlying work; it just stops the UI waiting. The
 * underlying promise is kept handled so a late loss can't become an unhandled
 * rejection.
 * @template T
 * @param {Promise<T>} promise
 * @param {number} [ms]
 * @returns {Promise<T>}
 */
export function withOnlineTimeout(promise, ms = 12000) {
  const guarded = Promise.resolve(promise);
  guarded.catch(() => {}); // swallow a late loser so it never dangles unhandled
  let t;
  const timeout = new Promise((_, reject) => {
    t = setTimeout(() => {
      const e = new Error(TIMEOUT_MSG);
      e.code = 'offline/timeout';
      reject(e);
    }, ms);
  });
  return Promise.race([guarded, timeout]).finally(() => clearTimeout(t));
}

/**
 * Map any save-path error to a plain, user-safe message — raw Firebase strings
 * (e.g. "Failed to get document because the client is offline.") must never reach
 * the UI. Callers in the data layer return this instead of `err.message`.
 * @param {any} err
 * @returns {string}
 */
export function friendlySaveError(err) {
  const code = (err && err.code) || '';
  const msg = (err && err.message) || '';
  if (code === 'permission-denied') return "You don't have permission to make that change.";
  if (
    code === 'unavailable' ||
    code === 'offline/timeout' ||
    /offline|network|unavailable/i.test(msg)
  ) {
    return "You appear to be offline — we'll save it and sync when you're back online.";
  }
  return 'Couldn’t save. Please try again.';
}
